import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowsClockwise, Basketball, Lock, ShieldCheck } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import TeamLogo from "../components/TeamLogo";

const WNBA_ACCENT = "#FF6B35";

const pct = (n) => (n == null ? "—" : `${(n * 100).toFixed(1)}%`);
const money = (n) => {
  if (n === null || n === undefined) return "—";
  const v = Number(n);
  const sign = v > 0 ? "+" : v < 0 ? "−" : "";
  return `${sign}$${Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

function fmtGameTime(utc) {
  if (!utc) return "";
  try {
    const d = new Date(utc);
    return d.toLocaleString("en-US", {
      weekday: "short", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit", hour12: true,
      timeZone: "America/Los_Angeles",
    }) + " PT";
  } catch {
    return utc;
  }
}

function AnalystSummary({ name, summary, accent, badgeLetter }) {
  const wr = summary?.win_rate || 0;
  const picks = summary?.picks || 0;
  return (
    <div className="bg-[#121212] border rounded-sm p-5" style={{ borderColor: `${accent}55` }} data-testid={`wnba-analyst-${name.toLowerCase()}`}>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 rounded-sm flex items-center justify-center font-display text-2xl" style={{ background: accent, color: "#000" }}>
          {badgeLetter}
        </div>
        <div>
          <div className="font-display text-2xl tracking-wider">{name}</div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">{name === "Charlie" ? "Bounce-back · iso-1L" : "Win-continuation · WC"}</div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3 text-center">
        <div className="border border-white/10 rounded-sm p-3">
          <div className="text-[9px] uppercase tracking-widest text-zinc-500">Picks YTD</div>
          <div className="font-display text-2xl mt-0.5">{picks}</div>
        </div>
        <div className="border border-white/10 rounded-sm p-3">
          <div className="text-[9px] uppercase tracking-widest text-zinc-500">Record</div>
          <div className="font-display text-2xl mt-0.5">{summary?.wins || 0}-{summary?.losses || 0}</div>
        </div>
        <div className="border border-white/10 rounded-sm p-3">
          <div className="text-[9px] uppercase tracking-widest text-zinc-500">Hit Rate</div>
          <div className="font-display text-2xl mt-0.5" style={{ color: wr >= 0.6 ? "#22C55E" : wr >= 0.5 ? "#F59E0B" : "#FF3B30" }} data-testid={`wnba-analyst-${name.toLowerCase()}-wr`}>
            {pct(wr)}
          </div>
        </div>
      </div>
    </div>
  );
}

function UpcomingCard({ pick }) {
  const isCharlie = pick.analyst === "Charlie";
  const accent = isCharlie ? "#22C55E" : "#F59E0B";
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      className="bg-[#0F0F0F] border rounded-sm p-4 hover:bg-[#141414] transition-colors"
      style={{ borderColor: `${accent}40` }}
      data-testid={`wnba-upcoming-${pick.team_abbr}`}
    >
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-2">
        <div className="flex items-center gap-2">
          <Lock size={11} weight="fill" className="text-[#22C55E]" />
          <span className="text-[9px] uppercase tracking-[0.25em] font-bold text-[#22C55E]">LOCKED</span>
          <span className="text-[10px] uppercase tracking-widest font-bold" style={{ color: accent }}>{pick.analyst}</span>
        </div>
        <span className="text-[10px] text-zinc-500 font-mono-d">{fmtGameTime(pick.game_time_utc)}</span>
      </div>
      <div className="flex items-center gap-3 mb-1">
        <TeamLogo sport="wnba" abbr={pick.team_abbr} size={48} />
        <div className="flex-1 min-w-0">
          <div className="font-display text-3xl leading-none">{pick.team_abbr}</div>
          <div className="text-xs text-zinc-400 mt-1 truncate">
            <span className="font-bold">{pick.team}</span>
            <span className="text-zinc-500"> {pick.is_home ? "vs" : "@"} {pick.opponent}</span>
          </div>
        </div>
      </div>
      {pick.moneyline_used != null && (
        <div className="text-[10px] text-zinc-500 font-mono-d mb-1">
          ML <span className={pick.odds_provider === "fallback_-170" ? "text-zinc-400" : "text-zinc-200"}>{pick.moneyline_used > 0 ? "+" : ""}{pick.moneyline_used}</span>
          {pick.odds_provider && pick.odds_provider !== "fallback_-170" && <span className="text-zinc-600 ml-1">· {pick.odds_provider}</span>}
          {pick.odds_provider === "fallback_-170" && <span className="text-zinc-600 ml-1 italic">· est.</span>}
        </div>
      )}
      <div className="text-[11px] text-zinc-500 italic mt-2">{pick.recommendation}</div>
    </motion.div>
  );
}

function SettledRow({ pick }) {
  const isWin = pick.result === "W";
  const isLoss = pick.result === "L";
  const badge = isWin ? "bg-[#22C55E] text-black"
    : isLoss ? "bg-[#FF3B30] text-black"
    : "bg-zinc-700 text-zinc-300";
  return (
    <tr className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`wnba-settled-${pick.target_date}-${pick.team_abbr}`}>
      <td className="px-3 py-2 font-mono-d text-xs text-zinc-400">{pick.target_date}</td>
      <td className="px-3 py-2">
        <span className="text-[10px] uppercase tracking-widest font-bold" style={{ color: pick.analyst === "Charlie" ? "#22C55E" : "#F59E0B" }}>
          {pick.analyst}
        </span>
      </td>
      <td className="px-3 py-2 font-bold">
        <div className="flex items-center gap-2">
          <TeamLogo sport="wnba" abbr={pick.team_abbr} size={22} />
          {pick.team_abbr}
        </div>
      </td>
      <td className="px-3 py-2 text-xs text-zinc-400">
        <div className="flex items-center gap-1.5">
          <span>{pick.is_home ? "vs" : "@"}</span>
          <TeamLogo sport="wnba" abbr={pick.opponent_abbr} size={18} />
          <span>{pick.opponent_abbr}</span>
        </div>
      </td>
      <td className="px-3 py-2 text-xs font-mono-d text-zinc-500">
        {pick.final_score ? `${pick.final_score.team}-${pick.final_score.opponent}` : "—"}
      </td>
      <td className="px-3 py-2 text-right">
        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm font-bold ${badge}`}>{pick.result}</span>
      </td>
    </tr>
  );
}

export default function WnbaPublicPicks() {
  const { user } = useAuth();
  const [feed, setFeed] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (refresh = false) => {
    if (refresh) setRefreshing(true); else setLoading(true);
    try {
      const { data } = await api.get("/wnba/picks/feed?days=120");
      setFeed(data);
    } catch (e) {
      // noop — render empty state
    } finally {
      setLoading(false); setRefreshing(false);
    }
  };
  useEffect(() => { load(false); }, []);

  const isSubscriber = feed?.has_access;
  const isTrialing = user && (user.subscription_status === "trialing" || user.subscription_status === "trial" || user.subscription_status === "active");

  return (
    <div className="max-w-[1280px] mx-auto px-6 py-10">
      {/* Hero */}
      <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-11 h-11 rounded-sm flex items-center justify-center" style={{ background: `${WNBA_ACCENT}1F`, border: `1px solid ${WNBA_ACCENT}66` }}>
            <Basketball size={22} weight="fill" style={{ color: WNBA_ACCENT }} />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em]" style={{ color: WNBA_ACCENT }}>Accurate Monkey · WNBA</div>
            <h1 className="font-display text-5xl sm:text-6xl leading-none mt-1">WNBA PICKS</h1>
          </div>
        </div>
        <p className="text-sm text-zinc-400 max-w-3xl leading-relaxed">
          Daily WNBA picks from analysts <span className="text-white font-bold">Charlie</span> (iso-1L bounce-back) and{" "}
          <span className="text-white font-bold">Dakota</span> (win-continuation). Each pick is locked before tipoff —
          immutable, timestamped, tamper-evident.
        </p>
      </motion.div>

      {/* Trust strip */}
      <div className="bg-[#121212] border border-[#22C55E]/30 rounded-sm px-5 py-3 mb-8 flex items-center gap-3 flex-wrap">
        <ShieldCheck size={16} weight="fill" className="text-[#22C55E]" />
        <span className="text-[10px] uppercase tracking-[0.25em] text-[#22C55E] font-bold">Tamper-Evident</span>
        <span className="text-xs text-zinc-400">
          Every pick is written to an immutable ledger before tipoff. Hit rate is computed from <span className="text-white">settled, unfiltered</span> picks only.
        </span>
      </div>

      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-3xl">Season scorecard</h2>
        <button
          onClick={() => load(true)}
          disabled={loading || refreshing}
          data-testid="wnba-refresh"
          className="inline-flex items-center gap-1.5 px-3 py-2 border border-white/15 hover:border-white/40 text-xs uppercase tracking-widest rounded-sm transition-colors disabled:opacity-40"
        >
          <ArrowsClockwise size={12} className={refreshing ? "animate-spin" : ""} />
          {refreshing ? "Refreshing" : "Refresh"}
        </button>
      </div>

      {loading && !feed && (
        <div className="text-zinc-500 font-mono-d py-12 text-center text-sm">Loading WNBA picks…</div>
      )}

      {feed && (
        <>
          {/* Strategy paused banner — surfaces when both analysts have 0 active teams */}
          {feed.active_teams_count &&
            (feed.active_teams_count.charlie === 0 && feed.active_teams_count.dakota === 0) && (
            <div
              className="bg-yellow-500/10 border border-yellow-500/40 rounded-sm px-5 py-4 mb-6 flex items-center gap-3"
              data-testid="wnba-strategy-paused"
            >
              <ShieldCheck size={18} weight="fill" className="text-yellow-400 shrink-0" />
              <div className="text-xs sm:text-sm text-yellow-100/90 leading-relaxed">
                <span className="uppercase tracking-widest text-[10px] text-yellow-400 font-bold mr-2">Strategy paused</span>
                Both Charlie and Dakota currently have <span className="font-bold text-yellow-300">0 active WNBA teams</span>. No new picks will fire until the admin shortlists at least one team. Historical results below remain accurate.
              </div>
            </div>
          )}

          {/* Analyst cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">
            <AnalystSummary name="Charlie" summary={feed.summary.charlie} accent="#22C55E" badgeLetter="C" />
            <AnalystSummary name="Dakota" summary={feed.summary.dakota} accent="#F59E0B" badgeLetter="D" />
          </div>

          {/* Today's locked picks */}
          <h2 className="font-display text-3xl mb-4">Today's picks</h2>
          {feed.upcoming.length === 0 ? (
            <div className="bg-[#0F0F0F] border border-white/10 rounded-sm px-6 py-10 text-center mb-10" data-testid="wnba-upcoming-empty">
              <div className="text-sm text-zinc-400 mb-1">No qualifying picks today.</div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-600">
                Picks only fire when Charlie's iso-1L or Dakota's W1-after-L pattern triggers + the team plays today.
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-10" data-testid="wnba-upcoming-list">
              {feed.upcoming.map((p, i) => <UpcomingCard key={`${p.target_date}-${p.team_abbr}-${i}`} pick={p} />)}
            </div>
          )}

          {/* Subscriber upsell — only when user is anonymous or not subscribed */}
          {!isSubscriber && (
            <div className="bg-[#121212] border rounded-sm p-6 mb-10" style={{ borderColor: `${WNBA_ACCENT}66` }} data-testid="wnba-subscribe-cta">
              <div className="flex items-baseline justify-between flex-wrap gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em]" style={{ color: WNBA_ACCENT }}>Subscriber-only</div>
                  <div className="font-display text-2xl mt-1">Unlock pick economics</div>
                  <div className="text-sm text-zinc-400 mt-1 max-w-2xl">
                    Subscribers see stake size, target profit, $250 / -170 sizing, and full pick reasoning. Today's $15/week MLB subscription unlocks WNBA at no extra cost.
                  </div>
                </div>
                <Link
                  to={isTrialing ? "/account" : "/signup"}
                  className="px-5 py-2.5 text-xs uppercase tracking-widest font-bold rounded-sm transition-colors"
                  style={{ background: WNBA_ACCENT, color: "#000" }}
                  data-testid="wnba-subscribe-cta-btn"
                >
                  {isTrialing ? "Manage Account" : "Start 7-day free trial"}
                </Link>
              </div>
            </div>
          )}

          {/* Settled picks ledger */}
          <h2 className="font-display text-3xl mb-4">Track record</h2>
          {feed.settled.length === 0 ? (
            <div className="text-zinc-500 italic text-sm py-8 text-center" data-testid="wnba-settled-empty">
              No settled picks yet — track record starts as soon as the first WNBA pick clears.
            </div>
          ) : (
            <div className="bg-[#121212] border border-white/10 rounded-sm overflow-x-auto" data-testid="wnba-settled-table">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-white/10">
                  <tr>
                    <th className="text-left px-3 py-2">Date</th>
                    <th className="text-left px-3 py-2">Analyst</th>
                    <th className="text-left px-3 py-2">Team</th>
                    <th className="text-left px-3 py-2">vs</th>
                    <th className="text-left px-3 py-2">Score</th>
                    <th className="text-right px-3 py-2">Result</th>
                  </tr>
                </thead>
                <tbody>
                  {feed.settled.map((p, i) => <SettledRow key={`${p.target_date}-${p.team_abbr}-${i}`} pick={p} />)}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
